/**
 * FILE: assets/vehicle-garage.js
 *
 * The "garage": a single selected vehicle persisted in localStorage, shared by
 * every garage "surface" on the page. A surface is any element with class
 * .vg__surface that holds the form + result markup — currently the header modal
 * and the homepage "Find Your Fit" section. Every surface reads and writes the
 * SAME state, and all of them (plus the header pill) re-render off one
 * `vehicle:changed` event. So saving a vehicle anywhere updates everywhere.
 *
 * Config (window.YLGarageConfig): { fitment, models, proUrl, ogUrl }
 * Stored (localStorage "yl_vehicle"): { model, year, option, label, pro, og }
 */
(function () {
  'use strict';

  var KEY = 'yl_vehicle';
  var ADD_LABEL = 'Add a Vehicle';
  var OPTIONS_FALLBACK = 'Options';

  var Garage = {
    get: function () { try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch (e) { return null; } },
    set: function (v) { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) {} document.dispatchEvent(new CustomEvent('vehicle:changed', { detail: v })); },
    clear: function () { try { localStorage.removeItem(KEY); } catch (e) {} document.dispatchEvent(new CustomEvent('vehicle:changed', { detail: null })); }
  };
  window.YLGarage = Garage;

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    var cfg = window.YLGarageConfig;
    if (!cfg) return;

    var data = cfg.fitment || {};
    var models = cfg.models || [];

    // ---- Header pill(s) reflect the stored vehicle (label only) ----
    function renderButtons() {
      var v = Garage.get();
      var label = (v && v.label) ? v.label : ADD_LABEL;
      document.querySelectorAll('.vg__button-label').forEach(function (el) { el.textContent = label; });
      document.querySelectorAll('.vg-chip, .vg-bar').forEach(function (el) { el.classList.toggle('vg--has-vehicle', !!(v && v.label)); });
    }

    // ---- One garage surface (modal, homepage section, ...) ----
    // Each surface owns its own DOM refs + in-progress dropdown state, but they
    // all persist to and read from the same Garage.
    function initSurface(root) {
      var title = root.querySelector('.vg__title');
      var form = root.querySelector('.vg__form');
      var modelSel = root.querySelector('.vg__model');
      var yearSel = root.querySelector('.vg__year');
      var optField = root.querySelector('.vg__field--options');
      var optSel = root.querySelector('.vg__options');
      var optLabel = root.querySelector('.vg__options-label');
      var saveBtn = root.querySelector('.vg__save');

      var result = root.querySelector('.vg__result');
      var resultVehicle = root.querySelector('.vg__result-vehicle');
      var removeBtn = root.querySelector('.vg__remove');
      var products = root.querySelector('.vg__products');
      var placeholder = root.querySelector('.vg__placeholder');

      if (!form || !modelSel || !yearSel || !saveBtn) return null;

      var state = { list: null, needsOption: false };

      // ---- Dropdown helpers ----
      function ph(sel, text) {
        sel.innerHTML = '';
        var o = document.createElement('option');
        o.value = ''; o.textContent = text; o.disabled = true; o.selected = true;
        sel.appendChild(o);
      }
      function addOpt(sel, value, text) {
        var o = document.createElement('option');
        o.value = value; o.textContent = text;
        sel.appendChild(o);
      }
      function optionDisplay(full) {
        return full && full.indexOf(': ') > -1 ? full.split(': ').slice(1).join(': ') : full;
      }
      function buildOptions(list) {
        var type = null;
        if (list.every(function (e) { return e.o.indexOf(': ') > -1; })) {
          var prefixes = list.map(function (e) { return e.o.split(': ')[0]; });
          if (prefixes.every(function (p) { return p === prefixes[0]; })) type = prefixes[0];
        }
        if (optLabel) optLabel.textContent = type || OPTIONS_FALLBACK;
        ph(optSel, 'Select ' + (type || OPTIONS_FALLBACK).toLowerCase());
        list.forEach(function (e) { addOpt(optSel, e.o, optionDisplay(e.o)); });
      }
      function fillModels() {
        ph(modelSel, 'Select model');
        models.forEach(function (m) { addOpt(modelSel, m, m); });
      }

      // ---- Handlers ----
      function onModel() {
        if (optField) optField.hidden = true;
        state.list = null; state.needsOption = false;
        ph(yearSel, 'Select year');
        var m = modelSel.value;
        if (m && data[m]) { Object.keys(data[m]).forEach(function (y) { addOpt(yearSel, y, y); }); yearSel.disabled = false; }
        else yearSel.disabled = true;
        updateSave();
      }
      function onYear() {
        if (optField) optField.hidden = true;
        state.list = null; state.needsOption = false;
        var m = modelSel.value, y = yearSel.value;
        if (m && y) {
          state.list = data[m][y] || [];
          if (state.list.length > 1) { state.needsOption = true; buildOptions(state.list); if (optField) optField.hidden = false; }
        }
        updateSave();
      }
      function updateSave() {
        saveBtn.disabled = !(modelSel.value && yearSel.value && (!state.needsOption || (optSel && optSel.value)));
      }
      function currentEntry() {
        if (!state.list) return null;
        if (state.needsOption) {
          for (var i = 0; i < state.list.length; i++) if (state.list[i].o === optSel.value) return state.list[i];
          return null;
        }
        return state.list[0];
      }
      function onSave() {
        var entry = currentEntry();
        if (!entry) return;
        var parts = [yearSel.value, modelSel.value];
        var optText = state.needsOption ? optionDisplay(optSel.value) : '';
        if (optText && optText !== 'N/A') parts.push(optText);
        // Persist → fires vehicle:changed → every surface re-renders.
        Garage.set({
          model: modelSel.value, year: yearSel.value,
          option: state.needsOption ? optSel.value : null,
          label: parts.filter(Boolean).join(' '),
          pro: entry.pro || null, og: entry.og || null
        });
      }
      function onRemove() { Garage.clear(); }

      function resetForm() {
        fillModels();
        ph(yearSel, 'Select year'); yearSel.disabled = true;
        if (optField) optField.hidden = true;
        state.list = null; state.needsOption = false;
        updateSave();
      }

      // ---- Result: point each product card at its fitting variant ----
      function setCard(kind, variant) {
        var card = root.querySelector('.vg__card[data-kind="' + kind + '"]');
        if (!card) return;
        var url = card.getAttribute('data-product-url');
        if (variant && url) {
          var sep = url.indexOf('?') > -1 ? '&' : '?';
          card.href = url + sep + 'variant=' + encodeURIComponent(variant);
          card.classList.remove('vg__card--disabled');
          card.removeAttribute('aria-disabled');
        } else {
          card.removeAttribute('href');
          card.classList.add('vg__card--disabled');
          card.setAttribute('aria-disabled', 'true');
        }
      }
      function fillResult(v) {
        if (resultVehicle) resultVehicle.textContent = v.label;
        var bothNull = !v.pro && !v.og;
        if (products) products.hidden = bothNull;
        if (placeholder) placeholder.hidden = !bothNull;
        if (!bothNull) { setCard('pro', v.pro); setCard('og', v.og); }
      }

      // ---- Swap form vs result to match the shared state ----
      function render() {
        var v = Garage.get();
        var has = !!(v && v.label);
        if (form) form.hidden = has;
        if (result) result.hidden = !has;
        if (title) title.textContent = has ? 'My Vehicle' : 'Find Your Fit';
        if (has) fillResult(v);
        else resetForm();
      }

      modelSel.addEventListener('change', onModel);
      yearSel.addEventListener('change', onYear);
      if (optSel) optSel.addEventListener('change', updateSave);
      saveBtn.addEventListener('click', onSave);
      if (removeBtn) removeBtn.addEventListener('click', onRemove);

      return { render: render };
    }

    var surfaces = [];
    document.querySelectorAll('.vg__surface').forEach(function (el) {
      var s = initSurface(el);
      if (s) surfaces.push(s);
    });
    if (!surfaces.length) { renderButtons(); return; }

    function renderAll() {
      surfaces.forEach(function (s) { s.render(); });
      renderButtons();
    }

    renderAll();
    document.addEventListener('vehicle:changed', renderAll);
  });
})();
